/**
 * Превью записи ВКонтакте: аватар и название сообщества, текст с переносами и разметкой ВК
 * ([club…|…], #хэштеги, ссылки), сворачивание «Показать полностью», раскладка фото 1 / 2 / 1+N.
 */
import { Fragment, useMemo, useState } from "react";
import type { PostImage } from "@impuls/shared";
import { tokenizeVkText, vkCollapsePoint } from "@impuls/shared";

function VkText({ text }: { text: string }) {
  const segs = useMemo(() => tokenizeVkText(text), [text]);
  return (
    <>
      {segs.map((s, i) =>
        s.type === "text" ? (
          <Fragment key={i}>{s.text}</Fragment>
        ) : s.type === "hashtag" ? (
          <span key={i} className="vk-link">
            {s.text}
          </span>
        ) : (
          <a key={i} className="vk-link" href={s.href} target="_blank" rel="noreferrer">
            {s.text}
          </a>
        ),
      )}
    </>
  );
}

export function VkPreview({
  text,
  images,
  groupName,
  groupPhoto,
  when,
  emptyHint = "Текст появится здесь, когда Claude напишет пост",
}: {
  text: string;
  images: PostImage[];
  groupName: string;
  groupPhoto?: string | null;
  when?: string;
  emptyHint?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const cut = useMemo(() => vkCollapsePoint(text), [text]);
  const shown = !expanded && cut !== null ? text.slice(0, cut).trimEnd() : text;
  return (
    <article className="vk-post" aria-label="Так пост будет выглядеть во ВКонтакте">
      <header className="vk-post__head">
        <span className="vk-post__avatar">
          <img src={groupPhoto || "/brand/heart-red.png"} alt="" />
        </span>
        <div className="vk-post__who">
          <div className="vk-post__name">{groupName}</div>
          <div className="vk-post__time">{when || "только что"}</div>
        </div>
        <span className="vk-post__dots" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <circle cx="5" cy="12" r="1.8" />
            <circle cx="12" cy="12" r="1.8" />
            <circle cx="19" cy="12" r="1.8" />
          </svg>
        </span>
      </header>
      {text.trim() ? (
        <div className="vk-post__text">
          <VkText text={shown} />
          {!expanded && cut !== null && (
            <>
              {"…"}
              <br />
              <button className="vk-more" onClick={() => setExpanded(true)}>
                Показать полностью
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="vk-post__text vk-post__text--empty">{emptyHint}</div>
      )}
      {images.length > 0 && <VkPhotos images={images} />}
      <footer className="vk-post__foot" aria-hidden="true">
        <span className="vk-act">
          <svg viewBox="0 0 24 24">
            <path d="M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0112 7.2a4.3 4.3 0 017.5 2.6C19.5 15.4 12 20 12 20z" />
          </svg>
        </span>
        <span className="vk-act">
          <svg viewBox="0 0 24 24">
            <path d="M5 6.5A2.5 2.5 0 017.5 4h9A2.5 2.5 0 0119 6.5v7a2.5 2.5 0 01-2.5 2.5H11l-4.5 3.5V16h0A2.5 2.5 0 015 13.5z" />
          </svg>
        </span>
        <span className="vk-act">
          <svg viewBox="0 0 24 24">
            <path d="M13 5l7 6.5-7 6.5v-4c-5 0-8 1.5-9 5 0-6 3-9.5 9-10z" />
          </svg>
        </span>
        <span className="vk-views">
          <svg viewBox="0 0 24 24">
            <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          0
        </span>
      </footer>
    </article>
  );
}

function VkPhotos({ images }: { images: PostImage[] }) {
  const n = images.length;
  if (n === 1) {
    return (
      <div className="vk-photos vk-photos--1">
        <img src={images[0].url} alt="" />
      </div>
    );
  }
  if (n === 2) {
    return (
      <div className="vk-photos vk-photos--2">
        {images.map((i) => (
          <img key={i.id} src={i.url} alt="" />
        ))}
      </div>
    );
  }
  const [first, ...rest] = images;
  const tail = rest.slice(0, 4);
  return (
    <div className="vk-photos vk-photos--many">
      <img className="vk-photos__main" src={first.url} alt="" />
      <div className="vk-photos__row" style={{ gridTemplateColumns: `repeat(${tail.length}, 1fr)` }}>
        {tail.map((i, idx) => (
          <div key={i.id} className="vk-photos__cell">
            <img src={i.url} alt="" />
            {idx === tail.length - 1 && n > 5 && <span className="vk-photos__more">+{n - 5}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
